import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../utils/userApi";

const countWords = (text) =>
  text.trim()
    ? text
        .trim()
        .split(/\s+/)
        .filter((w) => w.length > 0).length
    : 0;

const FIELDS = [
  {
    key: "bioIntro",
    label: "Short Introduction",
    minWords: 20,
    maxWords: 60,
    required: true,
    tutorPlaceholder: "Briefly introduce yourself to prospective students...",
    institutionPlaceholder: "Briefly introduce your training centre...",
  },
  {
    key: "bioExperience",
    label: "Experience",
    minWords: 0,
    maxWords: 120,
    required: false,
    tutorPlaceholder: "Share your teaching experience and background...",
    institutionPlaceholder: "Share your training centre's track record...",
  },
  {
    key: "bioQualifications",
    label: "Qualifications",
    minWords: 0,
    maxWords: 100,
    required: false,
    tutorPlaceholder: "List your degrees, certifications, and licenses...",
    institutionPlaceholder: "List your centre's accreditations and certifications...",
  },
  {
    key: "bioTeachingApproach",
    label: "Teaching Approach",
    minWords: 0,
    maxWords: 100,
    required: false,
    tutorPlaceholder: "Describe how you teach and what makes your lessons effective...",
    institutionPlaceholder: "Describe your teaching methodology...",
  },
];

const HEADLINE_MAX = 120;
const AVAILABILITY_MAX = 200;

const BioForm = ({ onCancel, setStatusModal }) => {
  const { user, updateUser } = useAuth();
  const isInstitution = user?.role === "institution";

  const [values, setValues] = useState({
    headline: user?.headline || "",
    availability: user?.availability || "",
    bioIntro: user?.bioIntro || user?.about || "",
    bioExperience: user?.bioExperience || "",
    bioQualifications: user?.bioQualifications || "",
    bioTeachingApproach: user?.bioTeachingApproach || "",
  });

  const handleChange = (key, maxWords) => (e) => {
    const value = e.target.value;
    if (countWords(value) <= maxWords || value === "") {
      setValues((prev) => ({ ...prev, [key]: value }));
    }
  };

  const introWordCount = countWords(values.bioIntro);
  const isIntroTooShort = introWordCount > 0 && introWordCount < FIELDS[0].minWords;
  const isIntroEmpty = introWordCount === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isIntroEmpty || isIntroTooShort) return;

    try {
      const res = await updateUserProfile(values, user.token);
      updateUser(res.user);

      setStatusModal({
        open: true,
        type: "success",
        message: "Bio Updated",
        subtext: "Your bio has been updated successfully.",
      });

      onCancel();
    } catch (err) {
      console.error(err);
      setStatusModal({
        open: true,
        type: "error",
        message: "Error Updating Bio",
        subtext: "There was an error updating your bio. Please try again.",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form-card">
      <div className="form-group">
        <label>Professional Headline (optional)</label>
        <input
          type="text"
          value={values.headline}
          maxLength={HEADLINE_MAX}
          onChange={(e) =>
            setValues((prev) => ({ ...prev, headline: e.target.value }))
          }
          placeholder={
            isInstitution
              ? "e.g. Award-winning JAMB & WAEC prep centre in Abuja"
              : "e.g. JAMB & WAEC Mathematics Specialist, 8 yrs experience"
          }
        />
        <div
          style={{ marginTop: "6px", fontSize: "13px", textAlign: "right", color: "#888" }}
        >
          {values.headline.length} / {HEADLINE_MAX}
        </div>
      </div>

      <div className="form-group">
        <label>Availability (optional)</label>
        <input
          type="text"
          value={values.availability}
          maxLength={AVAILABILITY_MAX}
          onChange={(e) =>
            setValues((prev) => ({ ...prev, availability: e.target.value }))
          }
          placeholder="e.g. Weekday evenings, Saturdays"
        />
        <div
          style={{ marginTop: "6px", fontSize: "13px", textAlign: "right", color: "#888" }}
        >
          {values.availability.length} / {AVAILABILITY_MAX}
        </div>
      </div>

      {FIELDS.map((field) => {
        const wordCount = countWords(values[field.key]);
        const tooShort =
          field.required && wordCount > 0 && wordCount < field.minWords;

        return (
          <div className="form-group" key={field.key}>
            <label>
              {field.label}
              {field.required ? "" : " (optional)"}
            </label>

            <textarea
              value={values[field.key]}
              onChange={handleChange(field.key, field.maxWords)}
              rows={field.key === "bioIntro" ? 3 : 4}
              placeholder={
                isInstitution ? field.institutionPlaceholder : field.tutorPlaceholder
              }
            />

            <div
              style={{
                marginTop: "6px",
                fontSize: "13px",
                textAlign: "right",
                color:
                  wordCount >= field.maxWords
                    ? "#e24b4a"
                    : wordCount >= field.minWords
                      ? "#3b6d11"
                      : "#888",
              }}
            >
              {wordCount} / {field.maxWords} words
            </div>

            {field.key === "bioIntro" && isIntroEmpty && (
              <p style={{ fontSize: "12px", color: "#e24b4a", marginTop: "4px" }}>
                Short introduction is required (minimum {field.minWords} words)
              </p>
            )}
            {field.key === "bioIntro" && tooShort && (
              <p style={{ fontSize: "12px", color: "#e24b4a", marginTop: "4px" }}>
                {field.minWords - wordCount} more word
                {field.minWords - wordCount !== 1 ? "s" : ""} needed (minimum{" "}
                {field.minWords})
              </p>
            )}
          </div>
        );
      })}

      <div className="form-actions">
        <button type="button" className="edit-cancel-btn" onClick={onCancel}>
          Cancel
        </button>

        <button
          type="submit"
          className="edit-primary-btn"
          disabled={isIntroEmpty || isIntroTooShort}
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default BioForm;
